/**
 * EIP-6963 (Multi Injected Provider Discovery).
 *
 * Before EIP-6963 there was exactly one `window.ethereum`, so every injected wallet
 * fought over the same slot and the last extension to load won. That is why the old
 * connector setup could only ever offer a single generic "Injected" option, and why
 * the UIKit's MetaMask / TrustWallet / MathWallet / TokenPocket / SafePal tiles all
 * resolved to the same provider regardless of which one the user picked.
 *
 * EIP-6963 replaces that with an announcement protocol: the page dispatches
 * `eip6963:requestProvider`, and every installed wallet answers with an
 * `eip6963:announceProvider` event carrying its own provider handle plus metadata
 * (name, icon, rdns). That lets us list the wallets the user actually has - MetaMask,
 * Rabby, OKX, Trust, Binance Web3, Coinbase, Phantom - as distinct, correctly-labelled
 * entries, and connect to the exact one they clicked.
 *
 * Wallets announce asynchronously and may announce late (an extension that finishes
 * initialising after our first request), so this module keeps a live registry and
 * notifies subscribers rather than resolving a one-shot promise.
 */

export interface Eip6963ProviderInfo {
  uuid: string
  name: string
  icon: string
  rdns: string
}

export interface Eip6963Provider {
  request: (args: { method: string; params?: unknown[] | Record<string, unknown> }) => Promise<unknown>
  on?: (event: string, listener: (...args: any[]) => void) => void
  removeListener?: (event: string, listener: (...args: any[]) => void) => void
}

export interface Eip6963ProviderDetail {
  info: Eip6963ProviderInfo
  provider: Eip6963Provider
}

const registry = new Map<string, Eip6963ProviderDetail>()
const subscribers = new Set<(details: Eip6963ProviderDetail[]) => void>()
let listening = false

function snapshot(): Eip6963ProviderDetail[] {
  return Array.from(registry.values())
}

function emit() {
  const details = snapshot()
  subscribers.forEach((fn) => fn(details))
}

function handleAnnounce(event: Event) {
  const { detail } = event as CustomEvent<Eip6963ProviderDetail>
  if (!detail?.info?.rdns || !detail?.provider) return

  // rdns is the stable identity for a wallet; uuid is regenerated per page load, so
  // keying on rdns avoids listing the same wallet twice if it announces more than once.
  const existing = registry.get(detail.info.rdns)
  if (existing && existing.provider === detail.provider) return

  registry.set(detail.info.rdns, detail)
  emit()
}

/** Begin listening and ask any installed wallets to announce themselves. */
export function startEip6963Discovery() {
  if (typeof window === 'undefined') return

  if (!listening) {
    window.addEventListener('eip6963:announceProvider', handleAnnounce)
    listening = true
  }

  window.dispatchEvent(new Event('eip6963:requestProvider'))
}

/**
 * Subscribe to the discovered-wallet list. Fires immediately with what is already
 * known, then again whenever a new wallet announces. Returns an unsubscribe function.
 */
export function subscribeToEip6963(callback: (details: Eip6963ProviderDetail[]) => void): () => void {
  subscribers.add(callback)
  callback(snapshot())

  // Re-request on each new subscriber: cheap, and catches wallets that installed or
  // finished initialising after the initial discovery pass.
  startEip6963Discovery()

  return () => {
    subscribers.delete(callback)
  }
}

export function getEip6963Providers(): Eip6963ProviderDetail[] {
  return snapshot()
}

export function findEip6963ProviderByRdns(rdns: string): Eip6963ProviderDetail | undefined {
  return registry.get(rdns)
}
